import React, { useEffect, useMemo, useState } from "react";
import { ModalHeader, ModalContent, Modal, Grid } from "semantic-ui-react";
import { CustomButton, DropDown, InputText } from "../../../../../components";
import { useForm } from "react-hook-form";
import { useGetFacilityWholeList } from "../../../../../api/cultivations";
import { errorView } from "../../../../../helpers/ErrorHandler";
import { NodeProps } from "reactflow";
import { NodeLabelExistError } from "../../../../../config/enums/NodeLabelError.enum";
import { isNodeLabelExists } from "../../../../../components/drawing-tool/utils";


interface ProcessGateFormModalProps {
  setProcessGateNode: (node: NodeProps | null, isCreated: boolean) => void;
  newNode?: any;
  edit?: boolean;
  processGateName?: any;
  updateProcessGate: (processGateName: string, newNode: any) => void;
  createProcessGate: (processGateName: string, newNode: any) => void;
  chartNodes: any[];
}

const ProcessGateFormModal: React.FC<ProcessGateFormModalProps> = ({
  setProcessGateNode,
  newNode,
  edit,
  processGateName,
  updateProcessGate,
  createProcessGate,
  chartNodes
}) => {
  const [facilityId, setFacilityId] = useState("");

  // Fetch facility list
  const { data: facilityWholeList } = useGetFacilityWholeList("FACTORY");

  const defaultValues = useMemo(() => {
    return {
      processGateName: "",
      facilityName: "",
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    setError,
    setFocus
  } = useForm({
    mode: "all",
    defaultValues,
  });

  // Effect to initialize form data when the modal opens
  useEffect(() => {
    if (edit && newNode?.data) {
      setValue("processGateName", processGateName);
      setValue("facilityName", newNode.data.facilityId);
      setFacilityId(newNode.data.facilityId || "");
    }
  }, [processGateName, newNode, facilityWholeList]);

  const closeModal = () => {
    setProcessGateNode(null, false);
  };

  const onSubmit = (data: {
    processGateName: string;
    facilityName: string;

  }) => {
    // check node label already exists
    if (chartNodes && isNodeLabelExists(chartNodes, data?.processGateName)) {
      clearErrors("processGateName");
      setFocus("processGateName");
      setError("processGateName", {
        type: "manual",
        message: NodeLabelExistError.PROCESS_GATE
      });
      return;
    }
    if (!facilityId) {
      return errorView("Facility name is required");
    }
    const modifiedNode = {
      ...newNode,
      data: {
        ...newNode?.data,
        label: `Process Gate (${data.processGateName})`,
        facilityId: facilityId,
      },
    };
    if (edit) {
      updateProcessGate(data.processGateName, modifiedNode);
    } else {
      createProcessGate(data.processGateName, modifiedNode);
    }
  };

  return (
    <Modal
      closeIcon={edit ? true : false}
      onClose={closeModal}
      open={true}
      centered={false}
      dimmer="blurring"
    >
      <ModalHeader>Process Gate</ModalHeader>
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid>
            <Grid.Column computer={12} tablet={12} mobile={12}>
              <InputText
                register={register}
                labelName="Process Gate Name"
                name="processGateName"
                errors={errors.processGateName}
                required
                errorMessage="Process Gate Name is required"
              />
            </Grid.Column>

            <Grid.Column computer={8} tablet={8} mobile={16}>
              <DropDown
                labelName={"Facility Name*"}
                placeholder="Select Facility Name"
                currentData={facilityWholeList}
                defaultValue={facilityId}
                validate={true}
                required={true}
                errorMessage={"Facility name is required"}
                handleChangeState={(e: any, { value }: any) => {
                  setFacilityId(value);
                  setValue("facilityName", value);
                }}
              />
            </Grid.Column>

            <Grid.Column computer={4} tablet={4} mobile={4}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CustomButton theme="green" title={"Update"} type="submit" />
              </div>
            </Grid.Column>
          </Grid>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ProcessGateFormModal;
